import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';
import type { Initiative } from '@/types/initiative';

interface InitiativeDetailsInfoProps {
  initiative: Initiative;
  locale: Locale;
}

export function InitiativeDetailsInfo({ initiative, locale }: InitiativeDetailsInfoProps) {
  const isRTL = locale === 'ar';

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '--';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(dateStr));
  };

  const isWalk = initiative.type === 'walk_initiative';
  const walkType = initiative.type_details?.walking_area_type;
  const pathsCount = initiative.paths?.length ?? 0;
  const locationsCount = initiative.locations?.length ?? 0;
  const totalPathDistance = initiative.paths?.reduce((sum, p) => sum + (p.total_distance_meters ?? 0), 0) ?? 0;

  // Every icon on this page follows the initiative's own type color — no other accent colors.
  const accentText = isWalk
    ? 'text-brand-blue'
    : initiative.type === 'juthoor_initiative'
    ? 'text-brand-green'
    : 'text-secondary';
  const iconBg = isWalk
    ? `bg-brand-blue/10 ${accentText}`
    : initiative.type === 'juthoor_initiative'
    ? `bg-brand-green/10 ${accentText}`
    : `bg-secondary/10 ${accentText}`;
  const accentHoverBorder = isWalk
    ? 'hover:border-brand-blue/20'
    : initiative.type === 'juthoor_initiative'
    ? 'hover:border-brand-green/20'
    : 'hover:border-secondary/20';

  const infoCards = [
    {
      icon: 'category',
      iconBg,
      label: t(locale, 'initiativeType'),
      value: initiative.type || initiative.category || '--',
    },
    {
      icon: 'location_on',
      iconBg,
      label: t(locale, 'locationScope'),
      value: [
        locale === 'ar'
          ? (initiative as any).city?.name_ar
          : (initiative as any).city?.name_en,
        (initiative as any).scope
          ? locale === 'ar'
            ? ({ cooperative: 'تعاونية', individual: 'فردية', institutional: 'مؤسسية' } as Record<string, string>)[(initiative as any).scope]
            : ({ cooperative: 'Cooperative', individual: 'Individual', institutional: 'Institutional' } as Record<string, string>)[(initiative as any).scope]
          : null,
      ]
        .filter(Boolean)
        .join(' - ') || '--',
    },
    ...(isWalk ? [
      {
        icon: walkType === 'path' ? 'route' : 'explore',
        iconBg,
        label: t(locale, 'walkingAreaType'),
        value: walkType === 'path' ? t(locale, 'structuredPath') : t(locale, 'openExploration'),
      },
    ] : []),
    {
      icon: 'calendar_today',
      iconBg,
      label: t(locale, 'timeline'),
      value:
        initiative.start_date && initiative.end_date
          ? `${formatDate(initiative.start_date)} - ${formatDate(initiative.end_date)}`
          : initiative.start_date
          ? formatDate(initiative.start_date)
          : '--',
    },
    ...(isWalk && locationsCount > 0 ? [
      {
        icon: 'pin_drop',
        iconBg,
        label: t(locale, 'locationsCount'),
        value: String(locationsCount),
      },
    ] : []),
    ...(isWalk && pathsCount > 0 ? [
      {
        icon: 'directions_walk',
        iconBg,
        label: t(locale, 'pathsCount'),
        value: `${pathsCount} (${Math.round(totalPathDistance)}${t(locale, 'meters')})`,
      },
    ] : []),
    ...(isWalk && initiative.participant_max > 0 ? [
      {
        icon: 'groups',
        iconBg,
        label: t(locale, 'participantLimit'),
        value: String(initiative.participant_max),
      },
    ] : []),
    ...(isWalk && initiative.goal_time > 0 ? [
      {
        icon: 'timer',
        iconBg,
        label: t(locale, 'goalTimeLimit'),
        value: `${initiative.goal_time} ${t(locale, 'months')}`,
      },
    ] : []),
  ];

  return (
    <div className="space-y-8">
      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {infoCards.map((card) => (
          <div
            key={card.label}
            className="bg-card p-6 rounded-lg shadow-sm border border-border flex items-center gap-4"
          >
            <div className={`w-12 h-12 ${card.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
              <span className="material-icons-outlined">{card.icon}</span>
            </div>
            <div>
              <p className={`text-xs text-muted-foreground font-medium mb-1 uppercase tracking-wider ${isRTL ? 'text-right' : ''}`}>
                {card.label}
              </p>
              <p className={`text-base font-bold text-foreground ${isRTL ? 'text-right' : ''}`}>
                {card.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Goal & Reward Structure */}
      <section className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
        <div className="p-6 border-b border-border flex items-center gap-2 bg-slate-50/50/30">
          <span className={`material-icons-outlined ${accentText}`}>emoji_events</span>
          <h3 className="text-base font-bold text-foreground">
            {t(locale, 'goalStructure')}
          </h3>
        </div>
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                label: t(locale, 'mainGoal'),
                value:
                  initiative.goal && initiative.goal_time
                    ? `${initiative.goal} ${locale === 'ar' ? 'نقاط كل' : 'NC every'} ${initiative.goal_time} ${locale === 'ar' ? 'يوم' : 'day'}`
                    : initiative.goal || '--',
              },
              {
                label: t(locale, 'pointsPerGoal'),
                value: initiative.points_for ? `${initiative.points_for} GP` : initiative.reward ? `${initiative.reward} GP` : '--',
              },
              {
                label: t(locale, 'conversionRateLabel'),
                value:
                  initiative.reward && initiative.nc
                    ? `${initiative.reward} GP = ${initiative.nc} NC`
                    : '--',
              },
              {
                label: locale === 'ar' ? 'الميزانية المتبقية' : 'Budget Remaining',
                value: initiative.budget ? `${initiative.budget} NC` : '--',
              },
            ].map((item) => (
              <div
                key={item.label}
                className={`text-center p-6 bg-muted/50 rounded-lg border border-transparent ${accentHoverBorder} transition-all`}
              >
                <p className={`text-sm text-foreground-secondary mb-3 ${isRTL ? 'text-right md:text-center' : ''}`}>
                  {item.label}
                </p>
                <p className={`text-xl font-bold ${accentText}`}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
